import {useCallback, useRef} from 'react'
import {getAIInfoText, getRandomId, processSSELines, processStreamData, processStream} from '../helpers'

const useEventHandlers = (
  // State values
  {userId, sessionId, isInitialized, prismProgram, activeRequestId, account},
  // State setters
  {setMessages, setIsRequestActive, setActiveRequestId},
  // Functions and refs
  {initializeSession, hideChatLoader, populateErrorResponse, handleAutoScrollToBottomFromHook, post}
) => {
  const processingRequestRef = useRef(false)
  const cancelledRef = useRef(false)
  const activeRequestIdRef = useRef(null)

  const handleQuerySubmit = useCallback(
    async (message) => {
      // More comprehensive check - if already processing or active request exists, ignore
      if (processingRequestRef.current || activeRequestId) {
        return
      }

      processingRequestRef.current = true
      cancelledRef.current = false
      let tempResponseId = null

      try {
        // Generate unique request ID for this specific request
        const currentRequestId = getRandomId()
        activeRequestIdRef.current = currentRequestId
        setActiveRequestId(currentRequestId)
        setIsRequestActive(true)

        // Early exit check after setting initial state
        if (cancelledRef.current) {
          return
        }

        let newSessionId = null
        if (!isInitialized || !sessionId) {
          newSessionId = await initializeSession()

          // Check if request was cancelled during session initialization
          if (cancelledRef.current) {
            return
          }

          if (!newSessionId) {
            populateErrorResponse({
              response: ['Oops, something went wrong on our end. Please try again in a few moments.']
            })
            setIsRequestActive(false)
            setActiveRequestId(null)
            hideChatLoader()
            return
          }
        }
        let normalizedUserId = userId
        if (normalizedUserId.includes('@identity.deloitte')) {
          normalizedUserId = normalizedUserId.split('@identity.deloitte')[0]
          normalizedUserId = normalizedUserId.replace(/_(?=[^_]*$)/, '@')
        }

        const requestData = {
          app_name: 'ask_healthprism',
          user_id: normalizedUserId,
          chat_id: newSessionId || sessionId,
          new_message: {
            parts: [{text: message}],
            role: 'user'
          },
          streaming: true
        }

        // Final check before API call - prevents duplicate calls
        if (cancelledRef.current) {
          return
        }

        // Create streaming message
        tempResponseId = getRandomId()
        const tempResponse = {
          id: tempResponseId,
          type: 'Bot',
          label: `${prismProgram} Agent`,
          responseText: '',
          feedback: -1,
          isStreaming: true,
          aiInfoText: getAIInfoText()
        }

        setMessages((prevMessages) => [...prevMessages, tempResponse])

        // One more check right before the actual API call
        if (cancelledRef.current) {
          // Remove the message we just added since we're cancelling
          setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== tempResponseId))
          return
        }

        // Add a tiny delay to allow cancellation to propagate
        await new Promise((resolve) => setTimeout(resolve, 10))

        // Final check after tiny delay - ensure this is still the active request
        if (cancelledRef.current || activeRequestIdRef.current !== currentRequestId) {
          // Remove the message we just added since we're cancelling
          setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== tempResponseId))
          return
        }
        const response = await post('/run_sse', requestData)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Create callbacks for the helper functions
        const streamCallbacks = {
          hideChatLoader,
          setActiveRequestId,
          setIsRequestActive,
          setMessages,
          populateErrorResponse,
          processingRequestRef,
          processSSELinesCallback: (lines, tempResponseId, accumulatedText) => {
            return processSSELines(lines, tempResponseId, accumulatedText, (data, tempResponseId, accumulatedText) => {
              return processStreamData(data, tempResponseId, accumulatedText, {
                setActiveRequestId,
                setMessages,
                handleAutoScrollToBottom: handleAutoScrollToBottomFromHook,
                hideChatLoader,
                setIsRequestActive,
                processingRequestRef
              })
            })
          },
          // Add callback to reset processing flag when stream completes
          onStreamComplete: () => {
            processingRequestRef.current = false
          }
        }

        await processStream(response, tempResponseId, streamCallbacks)
      } catch {
        setIsRequestActive(false)
        hideChatLoader()
        populateErrorResponse({
          response: ['Oops, something went wrong on our end. Please try again in a few moments.']
        })

        // Remove the specific streaming message that was created for this request
        if (tempResponseId) {
          setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== tempResponseId))
        }
      } finally {
        // Always clean up
        processingRequestRef.current = false
      }
    },
    [
      userId,
      sessionId,
      isInitialized,
      prismProgram,
      activeRequestId,
      setMessages,
      setIsRequestActive,
      setActiveRequestId,
      initializeSession,
      hideChatLoader,
      populateErrorResponse,
      handleAutoScrollToBottomFromHook,
      post
    ]
  )

  const abortCurrentRequest = useCallback(() => {
    cancelledRef.current = true
    processingRequestRef.current = false // Allow new requests immediately
    activeRequestIdRef.current = null // Clear the active request
    setIsRequestActive(false)
  }, [setIsRequestActive])

  return {
    handleQuerySubmit,
    abortCurrentRequest
  }
}
export default useEventHandlers

/*
CHATBOT EVENT HANDLERS HOOK

KEY FUNCTIONS:
- handleQuerySubmit: Main query processing function that handles session initialization, streaming requests, and error handling
- Manages SSE (Server-Sent Events) streaming for real-time chat responses
- Integrates with session management and message processing

FEATURES:
- Session initialization for new users
- Streaming response handling with real-time updates
- Error handling and recovery
- Message state management during requests
- Integration with AI info text and response formatting
*/
